import { useEffect, useRef, useCallback } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useTranslation } from 'react-i18next'

/**
 * Same driver.js + popover layout as in-game RoboTutor (`useRoboTutor`): character
 * header, `.tutorial-description`, and `updatePopoverStyles` on highlight so
 * `_driver.scss` matches the game tutor visually.
 *
 * Logic is decoupled from tutorial state / Redux; only the chrome is shared.
 *
 * `onDone` is invoked only when the user reaches the end of the tour and
 * clicks the final "Done" button. Closing / dismissing the tour invokes
 * `onDismiss` instead (so the onboarding can reappear on the next login).
 */
const WAIT_FOR_ELEMENT_TIMEOUT = 5000

const waitForElement = (selector, timeout = WAIT_FOR_ELEMENT_TIMEOUT) =>
  new Promise((resolve) => {
    const resolveEl = (el) => requestAnimationFrame(() => requestAnimationFrame(() => resolve(el)))
    const query = () =>
      typeof selector === 'function' ? selector() : document.querySelector(selector)

    const initial = query()
    if (initial) {
      resolveEl(initial)
      return
    }

    const observer = new MutationObserver(() => {
      const found = query()
      if (found) {
        observer.disconnect()
        resolveEl(found)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    setTimeout(() => {
      observer.disconnect()
      resolve(null)
    }, timeout)
  })

const CHARACTER_TOP_BASE = -20 // matches `.character-image` top in _driver.scss
const CHARACTER_EDGE_MARGIN = 16 // min gap kept between the avatar and the viewport edge
const CHARACTER_FLOAT_PX = 10 // matches the `float` keyframe translateY amplitude

// Same viewport clamp as useRoboTutor: shift the character's `top` back in when it clips past
// the top/bottom edge (one-shot — the onboarding tour doesn't smooth-scroll the board).
const clampCharacterToViewport = (image) => {
  requestAnimationFrame(() => {
    if (!image?.isConnected) return
    image.style.removeProperty('top') // reset to the CSS baseline before measuring
    const rect = image.getBoundingClientRect()
    const overTop = CHARACTER_EDGE_MARGIN + CHARACTER_FLOAT_PX - rect.top
    const overBottom =
      rect.bottom + CHARACTER_FLOAT_PX - (window.innerHeight - CHARACTER_EDGE_MARGIN)
    let shift = 0
    if (overTop > 0) shift = overTop
    else if (overBottom > 0) shift = -overBottom
    if (shift !== 0) {
      image.style.setProperty('top', `${CHARACTER_TOP_BASE + shift}px`, 'important')
    }
  })
}

// Duplicated from useRoboTutor — keeps popover-left/right + character-image sides in sync.
const updatePopoverStyles = (popover, configuredPosition, configuredMargin = null) => {
  if (!popover) return

  const isLeftPosition = configuredPosition === 'left'
  const popoverSide = isLeftPosition ? 'right' : 'left'
  const imageSide = isLeftPosition ? 'left' : 'right'

  popover.classList.remove('popover-left', 'popover-right')
  popover.classList.add(`popover-${popoverSide}`)

  const image = popover.querySelector('.character-image')
  image?.classList.remove('left-image', 'right-image')
  image?.classList.add(`${imageSide}-image`)

  Array.from(popover.classList).forEach((cls) => {
    if (/^m[trblxy]?-/.test(cls)) {
      popover.classList.remove(cls)
    }
  })

  if (configuredMargin) {
    popover.classList.add(...configuredMargin.split(' ').filter(Boolean))
  }

  if (image) clampCharacterToViewport(image)
}

const useOnboardingTour = ({ steps, active, onDone, onDismiss }) => {
  const { t } = useTranslation()
  const completedRef = useRef(false)

  const handleHighlighted = useCallback((element, step) => {
    const popover = document.querySelector('.driver-popover')
    const configuredPosition = step.popover?.position || 'right'
    const configuredMargin = step.popover?.margin || ''
    updatePopoverStyles(popover, configuredPosition, configuredMargin)
  }, [])

  useEffect(() => {
    if (!active || !steps?.length) return undefined

    completedRef.current = false

    let driverObj = null

    const config = {
      showProgress: true,
      allowClose: true,
      showCloseButton: true,
      overlayClickNext: false,
      nextBtnText: t('driver.next'),
      prevBtnText: t('driver.previous'),
      doneBtnText: t('onboarding.done'),
      steps,
      onCloseClick: () => {
        completedRef.current = false
        driverObj?.destroy()
      },
      onNextClick: (_element, _step, { state }) => {
        if (state.activeIndex === steps.length - 1) {
          completedRef.current = true
          driverObj?.destroy()
        } else {
          driverObj?.moveNext()
        }
      },
      onDestroyed: () => {
        if (completedRef.current) {
          onDone?.()
        } else {
          onDismiss?.()
        }
      },
      onHighlighted: handleHighlighted
    }

    driverObj = driver(config)

    let cancelled = false
    waitForElement(steps[0].element).then((el) => {
      if (cancelled) return
      if (!el) {
        driverObj?.destroy()
        return
      }
      driverObj?.drive()
      setTimeout(() => {
        if (!cancelled) driverObj?.refresh()
      }, 400)
    })

    return () => {
      cancelled = true
      driverObj?.destroy()
    }
  }, [active, steps, onDone, onDismiss, t, handleHighlighted])
}

export default useOnboardingTour
